import {observer} from "mobx-react";
import React, {useCallback, useEffect, useMemo, useState, useRef} from "react";
import {useLoadMorePagination} from "../../hooks/useLoadMorePagination";
import {
    BackButton, ContentArea,
    ContentAreaWrapper, DetailHeader, DetailHeaderLeft, EmptyStateContainer, LoadingContainer,FiltersRow
} from "./ActiveFolder.styled";
import {useDrivesStores} from "../../hook/use-drives.hook";
import {useLocation, useNavigate} from "react-router-dom";
import {useFolderHierarchy} from "../../hooks/useFolderHierarchy";
import Filters from "./components/Filters";
import {ButtonMenu, Icon, Typography, UploadFeedback, CircularProgress, Alert} from "@ann/web-components";
import UnifiedTable from '../../components/UnifiedTable';
import FoldersList from "../MyWorkspace/components/FoldersList";
import DocumentsList from "../MyWorkspace/components/DocumentsList";
import {useToast} from "../../../../../components/toast";
import EmptyDocumentState from "../../components/EmptyDocumentState";
import {UploadFeedbackWrapper} from "../../DrivesPage.styled";
import {useStore} from "../../../../../hooks";
import NewFolderModal from "../../components/NewFolderModal";
import RenameFolderModal from "../../components/RenameFolderModal";
import RenameDocumentModal from "../../components/RenameDocumentModal";
import FolderInformationSlideout from "../../components/FolderInformationSlideout/FolderInformationSlideout";
import { useStoreNotifications } from "../../hook/use-store-notifications.hook";
import { useFileUpload } from "../../hook/use-file-upload.hook";
import DeleteDocumentModal from "../../components/DeleteDocumentModal";


// ✅ UploadFeedbackSection - Observer component for upload feedback
const UploadFeedbackSection = observer(({ show, onClose, documentsStore, onRetry }) => {
    const handleFileRemove = useCallback((id) => {
        const doc = documentsStore.temporaryDocuments.find(d => d.uploadId === id);
        if (doc) {
            documentsStore.removeTemporaryDocument(id);
        }
    }, [documentsStore]);

    const handleRetry = useCallback((id) => {
        const doc = documentsStore.temporaryDocuments.find(d => d.uploadId === id);
        if (doc && onRetry) {
            onRetry(doc);
        }
    }, [documentsStore, onRetry]);

    // ✅ MobX observer rastrea automáticamente cambios en temporaryDocuments y uploadProgress
    const uploadFiles = documentsStore.temporaryDocuments.map(doc => {
        const progress = documentsStore.uploadProgress[doc.name];
        let status = 'ready';
        if (doc.state === 'loading') {
            status = 'uploading';
        } else if (doc.state === 'error') {
            status = 'uploadFailed';
        } else if (doc.state === 'active') {
            status = 'ready';
        }
        return {
            id: doc.uploadId,
            name: doc.name,
            progress: progress?.percentage || 0,
            status: status,
            onRetry: doc.state === 'error' ? () => handleRetry(doc.uploadId) : undefined
        };
    });

    if (!show || uploadFiles.length === 0) return null;

    return (
        <UploadFeedbackWrapper>
            <UploadFeedback
                files={uploadFiles}
                onClose={onClose}
                onFileRemove={handleFileRemove}
                onRetry={handleRetry}
            />
        </UploadFeedbackWrapper>
    );
});

UploadFeedbackSection.displayName = 'UploadFeedbackSection';

const ActiveFolderComponent = () => {
    const { activeFolderStore, myWorkspacesStore, drivesViewStore } = useDrivesStores();
    const { documentsStore } = useStore();
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useToast();
    // ✅ Use currentFolderId from useFolderHierarchy instead of useParams
    const { vaultUuid: vaultUuidFromHook, folderHierarchy: uuidHierarchy, currentFolderId: folderId, parentFolderId, isDriveContext } = useFolderHierarchy();

    const [folders, setFolders] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    // Track last loaded folderId to prevent redundant loads
    const lastLoadedFolderId = useRef(null);

    // Get drives count to avoid re-renders on array reference changes
    const drivesCount = drivesViewStore?.drivesStore?.drives?.length || 0;
    
    // ✅ PERFORMANCE: Extract first drive ID as primitive to avoid array dependency
    // Only extract if not in drive context (backward compatibility)
    const firstDriveId = useMemo(() => {
        return drivesCount > 0 ? drivesViewStore.drivesStore.drives[0]?.id : null;
    }, [drivesCount]);

    // ✅ PERFORMANCE: Memoize finalVaultUuid calculation to avoid duplication and re-renders
    // In drive context, use vaultUuidFromHook immediately (doesn't depend on drives loading)
    const finalVaultUuid = useMemo(() => {
        if (isDriveContext && vaultUuidFromHook) {
            return vaultUuidFromHook;
        }
        return firstDriveId;
    }, [isDriveContext, vaultUuidFromHook, firstDriveId]);

    // ✅ PERFORMANCE: Convert uuidHierarchy to string for stable dependency comparison
    const uuidHierarchyString = uuidHierarchy.join(',');

    // Load folder names from URL hierarchy
    useEffect(() => {
        const loadFolderNames = async () => {
            // ✅ Verify finalVaultUuid is available before loading
            if (uuidHierarchy.length > 0 && finalVaultUuid) {
                // Clear previous folder name to show loading
                activeFolderStore.setActiveFolderName('');
                
                // ✅ Use memoized finalVaultUuid
                const success = await activeFolderStore.loadFolderNames(uuidHierarchy, finalVaultUuid);
                
                // If folder not found, redirect to my-workspace
                if (!success) {
                    toast.error({ 
                        message: "Folder not found or has been deleted.", 
                        canClose: true 
                    });
                    navigate('/v2/drives/my-workspace');
                }
            }
        };
        loadFolderNames();
    }, [uuidHierarchyString, finalVaultUuid, navigate, toast]);

    // ✅ Reset lastLoadedFolderId when folderId or finalVaultUuid changes
    useEffect(() => {
        // Reset when folderId or finalVaultUuid changes to allow reloading
        lastLoadedFolderId.current = null;
    }, [folderId, finalVaultUuid]);

    // Load current folder content when folderId changes
    useEffect(() => {
        const loadData = async () => {
            // Only load if:
            // 1. We have a folderId
            // 2. We have a valid vaultUuid (finalVaultUuid)
            // 3. The folderId is different from the last loaded one (prevents redundant loads)
            if (folderId && finalVaultUuid && lastLoadedFolderId.current !== folderId) {
                setIsInitialLoading(true);
                lastLoadedFolderId.current = folderId; // Update last loaded folderId
                
                try {
                    // ✅ Use memoized finalVaultUuid
                    // Load the content of the current folder
                    const success = await activeFolderStore.setActiveFolder(folderId, finalVaultUuid);
                    
                    // If failed to load folder content, redirect to my-workspace
                    if (success === false) {
                        toast.error({ 
                            message: "Unable to access folder. It may have been deleted or you don't have permission.", 
                            canClose: true 
                        });
                        navigate('/v2/drives/my-workspace');
                    }
                } catch (error) {
                    console.error('Error loading folder data:', error);
                    toast.error({ 
                        message: "Error loading folder. Redirecting to My Workspace.", 
                        canClose: true 
                    });
                    navigate('/v2/drives/my-workspace');
                } finally {
                    setIsInitialLoading(false);
                }
            }
        };
        loadData();
    }, [folderId, finalVaultUuid, navigate, toast]);
    const [filterText, setFilterText] = useState("");
    const [sortBy, setSortBy] = useState("name");
    const [sortDirection, setSortDirection] = useState("asc");

    // ✅ Local state for document detail slideout
    const [isDocumentDetailOpen, setIsDocumentDetailOpen] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);

    // ✅ Local state to track if upload is in progress
    const [isUploading, setIsUploading] = useState(false);
    
    // ✅ Local state for upload feedback visibility
    const [showUploadFeedback, setShowUploadFeedback] = useState(false);
    
    // ✅ Local state for New Folder modal
    const [showNewFolderModal, setShowNewFolderModal] = useState(false);

    // ✅ State to handle the load of data after folder creation
    const [folderCreationStatus, setFolderCreationStatus] = useState(null);

    // ✅ State for RenameFolderModal
    const [isRenameFolderModalOpen, setIsRenameFolderModalOpen] = useState(false);
    const [folderToRename, setFolderToRename] = useState(null);
    const [isRenamingCurrentFolder, setIsRenamingCurrentFolder] = useState(false);

    const [isRenameDocumentModalOpen, setIsRenameDocumentModalOpen] = useState(false);
    const [documentToRename, setDocumentToRename] = useState(null);

    // ✅ State for FolderInformationSlideout
    const [isFolderInfoOpen, setIsFolderInfoOpen] = useState(false);
    const [selectedFolder, setSelectedFolder] = useState(null);

    const [alert, setAlert] = useState(null);

    // ✅ Toast notifications for Folder and Upload operations
    useStoreNotifications([
        {
            condition: myWorkspacesStore?.foldersStore?.createdFolder,
            type: 'success',
            description: `${myWorkspacesStore?.foldersStore?.folderCreated?.name} was created`,
            onClose: () => myWorkspacesStore?.foldersStore?.setCreatedFolder(false)
        },
        {
            condition: myWorkspacesStore?.foldersStore?.folderAlreadyExists,
            type: 'error',
            title: "A folder with this name already exists.",
            description: "Please choose a different name.",
            onClose: () => myWorkspacesStore?.foldersStore?.setFolderAlreadyExists(false)
        },
        {
            condition: myWorkspacesStore?.foldersStore?.isErrorCreateFolder,
            type: 'error',
            title: "Connection error, folder could not be created.",
            description: "Please try again or check your connection.",
            onClose: () => myWorkspacesStore?.foldersStore?.setIsErrorCreateFolder(false)
        },
        {
            condition: documentsStore?.uploadSuccess,
            type: 'success',
            title: "Upload successful",
            description: "Your documents have been uploaded successfully",
            onClose: () => documentsStore?.setUploadSuccess(false)
        },
        {
            condition: documentsStore?.uploadFailed,
            type: 'error',
            title: "Upload failed",
            description: documentsStore?.errorMessage || "Some files failed to upload",
            onClose: () => {
                documentsStore?.setUploadFailed(false);
                documentsStore?.clearErrorMessage();
            }
        }
    ]);
 
    // useEffect to handle the load of data after folder creation
    useEffect(() => {
        const reloadFolderContents = async () => {
            if (!folderCreationStatus) return;
            
            try {
                // only reload if the creation was successful
                if (folderCreationStatus.success) {
                    // Reload the content of the current folder (folderId)
                    // This synchronization with the local state is done automatically by the useEffect of sync
                    // ✅ Use memoized finalVaultUuid
                    await activeFolderStore.loadFolderContents(folderId, finalVaultUuid);
                }
            } catch (error) {
                toast.error({ message: "Error reloading folder contents", duration: 2, canClose: true });
            } finally {
                setFolderCreationStatus(null);
            }
        };
        
        reloadFolderContents();
    }, [folderCreationStatus, folderId, finalVaultUuid]);

    // Sync local state with store
    useEffect(() => {
        setFolders(activeFolderStore.folderList || []);
        setDocuments(activeFolderStore.documentList || []);
    }, [activeFolderStore.folderList, activeFolderStore.documentList]);

    // ✅ Show upload feedback when there are documents uploading
    useEffect(() => {
        if (documentsStore.temporaryDocuments.length > 0) {
            setShowUploadFeedback(true);
        }
    }, [documentsStore.temporaryDocuments.length]);

    // ✅ PERFORMANCE: Filter and sort locally with useMemo
    const filteredAndSortedFolders = useMemo(() => {
        let filtered = folders;

        if (filterText) {
            filtered = filtered.filter((folder) =>
                folder.name.toLowerCase().includes(filterText.toLowerCase())
            );
        }

        return filtered.slice().sort((a, b) => {
            const direction = sortDirection === "asc" ? 1 : -1;

            switch (sortBy) {
                case "name":
                    return direction * a.name.localeCompare(b.name);
                case "lastUpdated":
                    return direction * (new Date(a.updated_at) - new Date(b.updated_at));
                default:
                    return 0;
            }
        });
    }, [folders, filterText, sortBy, sortDirection]);

    const filteredAndSortedDocuments = useMemo(() => {
        let filtered = documents;

        if (filterText) {
            filtered = filtered.filter((doc) =>
                doc.name.toLowerCase().includes(filterText.toLowerCase())
            );
        }

        return filtered.slice().sort((a, b) => {
            const direction = sortDirection === "asc" ? 1 : -1;

            switch (sortBy) {
                case "name":
                    return direction * a.name.localeCompare(b.name);
                case "lastUpdated":
                    return direction * (new Date(a.updated_at) - new Date(b.updated_at));
                default:
                    return 0;
            }
        });
    }, [documents, filterText, sortBy, sortDirection]);

    // ✅ Pagination hooks with local filtered data
    const {
        visibleItems: visibleFolders,
        hasMore: hasMoreFolders,
        isLoading: isLoadingFolders,
        loadMore: loadMoreFolders,
    } = useLoadMorePagination(filteredAndSortedFolders, 8, 2000);

    const {
        visibleItems: visibleDocuments,
        hasMore: hasMoreDocuments,
        isLoading: isLoadingDocuments,
        loadMore: loadMoreDocuments,
    } = useLoadMorePagination(filteredAndSortedDocuments, 8, 2000);

    // ✅ Handle sort change
    const handleSortChange = useCallback(
        (field) => {
            if (sortBy === field) {
                setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
            } else {
                setSortBy(field);
                setSortDirection("asc");
            }
        },
        [sortBy]
    );

    const handleShareModalOpen = (item, type) => {
        const result = activeFolderStore.openShareModal(item, type);
        if (result?.message) {
            toast[result.variant]({
                title: result.title,
                message: result.message,
            });
        }
    };

    const handleAssignAgentModalOpen = (item) => {
        const result = activeFolderStore.openAssignAgentModal(item);
        if (result?.message) {
            toast[result.variant]({
                title: result.title,
                message: result.message,
            });
        }
    };

    const handleOpenDocumentDetail = (document) => {
        setSelectedDocument(document);
        setIsDocumentDetailOpen(true);
    };

    const handleUploadStart = useCallback(() => {
        // Switch to content view when upload starts
        setIsUploading(true);
    }, []);

    const handleUploadComplete = useCallback(async () => {
        // Reload folder contents after upload completes
        if (folderId && finalVaultUuid) {
            await activeFolderStore.loadFolderContents(folderId, finalVaultUuid);
        }
        setIsUploading(false);
    }, [folderId, finalVaultUuid, activeFolderStore]);

    // ✅ Initialize file upload hook with folder context
    // ✅ Use memoized finalVaultUuid
    const { fileInputRef, fileInputProps, openFileSelector, handleDrop, retryUpload } = useFileUpload({
        onUploadComplete: handleUploadComplete,
        onUploadStart: handleUploadStart,
        folderId: folderId, // Pass current folder ID for upload
        vaultUuid: finalVaultUuid // ✅ Pass vaultUuid when in drive context
    });

    // Handle back button navigation
    const handleBackNavigation = useCallback(() => {
        if (uuidHierarchy.length > 1) {
            // If there's more than one folder in hierarchy, navigate to parent
            // Build URL without the last UUID (current one)
            const parentHierarchy = uuidHierarchy.slice(0, -1);
            
            // ✅ Build URL based on context (drive or backward compatibility)
            if (isDriveContext && finalVaultUuid) {
                // Drive context: /v2/drives/d/vaultUuid/folder1/folder2...
                const parentUrl = parentHierarchy.length > 0
                    ? `/v2/drives/d/${finalVaultUuid}/${parentHierarchy.join('/')}`
                    : `/v2/drives/d/${finalVaultUuid}`;
                navigate(parentUrl);
            } else {
                // Backward compatibility: /v2/drives/folder1/folder2...
                const parentUrl = `/v2/drives/${parentHierarchy.join('/')}`;
                navigate(parentUrl);
            }
        } else if (uuidHierarchy.length === 1) {
            // If there's only one folder in hierarchy, go to drive root or My Workspace
            if (isDriveContext && finalVaultUuid) {
                // Drive context: go to drive root
                navigate(`/v2/drives/d/${finalVaultUuid}`);
            } else {
                // Backward compatibility: go to My Workspace
                navigate('/v2/drives/my-workspace');
            }
        } else {
            // No hierarchy, go to drive root or My Workspace
            if (isDriveContext && finalVaultUuid) {
                navigate(`/v2/drives/d/${finalVaultUuid}`);
            } else {
                navigate('/v2/drives/my-workspace');
            }
        }
    }, [uuidHierarchy, isDriveContext, finalVaultUuid, navigate]);
    
    const handleUploadDocumentsFromFolder = useCallback(() => {
        openFileSelector();
    }, [openFileSelector]);

    const handleOpenRenameDocumentModal = (document) => {
        setDocumentToRename(document);
        setIsRenameDocumentModalOpen(true);
    };

    const handleCloseRenameDocumentModal = () => {
        setIsRenameDocumentModalOpen(false);
        setDocumentToRename(null);
    };

    const handleSaveRenameDocument = (result) => {
        if (result?.success) {
            const newName = result.newName || result.updatedDocument?.name;
            const documentUuid =
                result.documentUuid ||
                result.updatedDocument?.documentUuid ||
                result.updatedDocument?.document_uuid ||
                documentToRename?.document_uuid ||
                documentToRename?.documentUuid ||
                documentToRename?.id;

            if (documentUuid && newName) {
                const updateDocumentName = (doc) => {
                    if (doc && typeof doc.setName === 'function') {
                        doc.setName(newName);
                    } else if (doc) {
                        doc.name = newName;
                    }
                };

                const docInStore = activeFolderStore.documentList.find(
                    (doc) =>
                        doc.document_uuid === documentUuid ||
                        doc.documentUuid === documentUuid ||
                        doc.id === documentUuid
                );

                if (docInStore) {
                    updateDocumentName(docInStore);
                }
                // ✅ REMOVED: No need to manually update local state
                // The useEffect will sync automatically from the store
            }
        }
    };

    const handleDeleteDocument = useCallback((document) => {
        activeFolderStore.openDeleteModal(document);
    }, [activeFolderStore]);

    const handleConfirmDelete = useCallback(async () => {
        let result;
        try {
            result = await activeFolderStore.deleteDocument();
        } catch (error) {
            result = { success: false, error: error.message || 'Unexpected error deleting document' };
        }
        
        activeFolderStore.closeDeleteModal();
        
        setTimeout(() => {
            if (result.success) {
                setAlert({
                    title: "Success",
                    message: result.message || "Document deleted successfully",
                    severity: "success"
                });
            } else {
                setAlert({
                    title: "Error",
                    message: result.error || result.message || 'Error deleting document',
                    severity: "error"
                });
            }
        }, 100);
    }, [activeFolderStore]);

    const handleCloseAlert = useCallback(() => {
        setAlert(null);
    }, []);

    // ✅ Handlers for RenameFolderModal
    const handleOpenRenameFolderModal = (folder) => {
        setFolderToRename(folder);
        setIsRenamingCurrentFolder(false);
        setIsRenameFolderModalOpen(true);
    };

    const handleOpenRenameCurrentFolder = () => {
        // Create a folder object for the current folder
        const currentFolder = {
            folder_uuid: folderId,
            name: activeFolderStore.nameFolder
        };
        setFolderToRename(currentFolder);
        setIsRenamingCurrentFolder(true);
        setIsRenameFolderModalOpen(true);
    };

    const handleCloseRenameFolderModal = () => {
        setIsRenameFolderModalOpen(false);
        setFolderToRename(null);
        setIsRenamingCurrentFolder(false);
    };

    const handleSaveRenameFolder = (result) => {
        if (result.success && result.updatedFolder) {
            if (isRenamingCurrentFolder) {
                // Update current folder name in the store
                activeFolderStore.setActiveFolderName(result.updatedFolder.name);
                // Update folder name in the breadcrumb map
                activeFolderStore.setFolderName(folderId, result.updatedFolder.name);
            } else {
                // Update folder in the list
                const folder = activeFolderStore.folderList.find(f => f.folder_uuid === result.folderUuid);
                if (folder) {
                    folder.setName(result.updatedFolder.name);
                }
            }
        }
        // Toast is handled by the modal
    };

    // ✅ Handler for FolderInformationSlideout
    const handleOpenFolderInfo = (folder) => {
        setSelectedFolder(folder);
        setIsFolderInfoOpen(true);
    };
    
    return (
        <>
        {alert && (
            <Alert
                title={alert.message}
                severity={alert.severity}
                onClose={handleCloseAlert}
                maxWidth="400px"
                closable={true}
                autoClose={true}
                autoCloseDelay={3000}
            />
        )}
        <ContentArea>
            <DetailHeader>
                <DetailHeaderLeft>
                    <BackButton onClick={handleBackNavigation}>
                        <Icon name='arrowLeft' size='medium' />
                    </BackButton>
                    <Typography category='title' weight='bold' size='large'>
                        {activeFolderStore.nameFolder || 'Loading...'}
                    </Typography>
                    <ButtonMenu
                        label=''
                        menuPosition="bottom"
                        onChange={(value) => {
                            if (value === 'upload_documents') {
                                handleUploadDocumentsFromFolder();
                            }
                            if (value === 'rename') {
                                handleOpenRenameCurrentFolder();
                            }
                            if (value === 'folder_information') {
                                // Create folder object for current folder
                                const currentFolderInfo = {
                                    folder_uuid: folderId,
                                    name: activeFolderStore.nameFolder
                                };
                                handleOpenFolderInfo(currentFolderInfo);
                            }
                        }}
                        size="small"
                        type="secondary"
                        width="auto">
                        <ButtonMenu.Menu icon={true}>
                            <ButtonMenu.Item
                                label="Upload Documents"
                                value="upload_documents"
                                icon='publish'
                            >
                                Upload Documents
                            </ButtonMenu.Item>
                            <ButtonMenu.Item
                                label="New Folder"
                                value="new_folder"
                                icon='createNewFolder'
                            >
                                New Folder
                            </ButtonMenu.Item>
                            <ButtonMenu.Item
                                label="Rename"
                                value="rename"
                                icon='edit'
                            >
                                Rename
                            </ButtonMenu.Item>
                            {/*<ButtonMenu.Item
                                label="Share"
                                value="share"
                                icon='personAdd'
                                disabled={true}
                            >
                                Share
                            </ButtonMenu.Item>
                            <ButtonMenu.Item
                                label="Move"
                                value="move"
                                icon='driveFileMove'
                                disabled={true}
                            >
                                Move
                            </ButtonMenu.Item>
                            <ButtonMenu.Item
                                label="Folder information"
                                value="folder_information"
                                icon="info"
                            >
                                Folder information
                            </ButtonMenu.Item>*/}
                        </ButtonMenu.Menu>
                    </ButtonMenu>
                </DetailHeaderLeft>
                <ButtonMenu
                    label="New"
                    menuPosition="bottom"
                    onChange={(value) => {
                        if (value === 'new-folder') {
                            setShowNewFolderModal(true);
                        }
                        if (value === 'upload-documents') {
                            handleUploadDocumentsFromFolder();
                        }
                    }}
                    size="medium"
                    type="primary"
                    width="auto"
                    customIcon="plus"
                >
                    <ButtonMenu.Menu icon={true} minWidth={"260px"}>
                        <ButtonMenu.Item
                            label="Upload Documents"
                            value="upload-documents"
                            icon='publish'
                        />
                        <ButtonMenu.Item
                            label="New Folder"
                            value="new-folder"
                            icon='createNewFolder'
                        />
                    </ButtonMenu.Menu>
                </ButtonMenu>
            </DetailHeader>
            
            {isInitialLoading ? (
                <LoadingContainer>
                    <CircularProgress />
                </LoadingContainer>
            ) : activeFolderStore.folderList.length > 0 || 
               activeFolderStore.documentList.length > 0 ||  
               isUploading || 
               documentsStore.temporaryDocuments.length > 0 ? (
                <>
                {activeFolderStore.selectionStore.countSelectedDocuments === 0 &&
                    activeFolderStore.selectionStore.countSelectedFolders === 0 && (
                        <FiltersRow>
                            <Filters
                                onFilterChange={setFilterText}
                                onSortChange={handleSortChange}
                                sortBy={sortBy}
                                sortDirection={sortDirection}
                                filterText={filterText}
                            />
                        </FiltersRow>
                    )
                }
                {activeFolderStore.viewMode === "table" ? (
                    <UnifiedTable
                        folders={visibleFolders}
                        documents={visibleDocuments}
                        selectionStore={activeFolderStore.selectionStore}
                        onShare={handleShareModalOpen}
                        onDuplicate={activeFolderStore.openDuplicateModal}
                        onAssignAgent={handleAssignAgentModalOpen}
                        onRenameFolder={handleOpenRenameFolderModal}
                        onRenameDocument={handleOpenRenameDocumentModal}
                        onFolderInfo={handleOpenFolderInfo}
                        onDelete={handleDeleteDocument}
                        hasMore={hasMoreDocuments}
                        isLoading={isLoadingDocuments}
                        onLoadMore={loadMoreDocuments}
                    />
                ) : (
                    <ContentAreaWrapper>
                        <>
                            <FoldersList
                                folders={visibleFolders}
                                selectionStore={activeFolderStore.selectionStore}
                                onShare={handleShareModalOpen}
                                onRename={handleOpenRenameFolderModal}
                                onDuplicate={activeFolderStore.openDuplicateModal}
                                onMove={(folderId) =>
                                    activeFolderStore.openMoveModal(folderId, "folder")
                                }
                                onFolderInfo={handleOpenFolderInfo}
                                onAssignAgent={handleAssignAgentModalOpen}
                                hasMore={hasMoreFolders}
                                isLoading={isLoadingFolders}
                                onLoadMore={loadMoreFolders}
                                hiddenActions={{
                                    share: true,
                                    duplicate: true,
                                    move: true,
                                    folderInfo: true
                                }}
                            />

                            <DocumentsList
                                documents={visibleDocuments}
                                selectionStore={activeFolderStore.selectionStore}
                                onShare={handleShareModalOpen}
                                onDuplicate={activeFolderStore.openDuplicateModal}
                                onRename={handleOpenRenameDocumentModal}
                                onMove={(documentId) =>
                                    activeFolderStore.openMoveModal(documentId, "document")
                                }
                                onDelete={handleDeleteDocument}
                                onAssignAgent={handleAssignAgentModalOpen}
                                onOpenDocumentDetail={handleOpenDocumentDetail}
                                hasMore={hasMoreDocuments}
                                isLoading={isLoadingDocuments}
                                onLoadMore={loadMoreDocuments}
                                onDropFiles={handleDrop}
                                hiddenActions={{
                                    download: true,
                                    rename: false,
                                    share: true,
                                    assignAgent: true,
                                    duplicate: true,
                                    move: true,
                                    delete: false
                                }}
                            />
                        </>
                    </ContentAreaWrapper>
                )}
                </>
            ) : (
                <EmptyStateContainer id="empty-state-container">
                    <EmptyDocumentState
                        folderId={folderId}
                        onUploadStart={handleUploadStart}
                        onUploadComplete={handleUploadComplete}
                    />
                </EmptyStateContainer>
            )}
        </ContentArea>
        
        {/* ✅ UploadFeedbackSection - Shows upload progress */}
        <UploadFeedbackSection 
            show={showUploadFeedback}
            onClose={() => setShowUploadFeedback(false)}
            documentsStore={documentsStore}
            onRetry={retryUpload}
        />
        
        {/* ✅ NewFolderModal - Create new folder */}
        <NewFolderModal
            isOpen={showNewFolderModal}
            onClose={() => setShowNewFolderModal(false)}
            onSave={(result) => {
                setFolderCreationStatus(result);
            }}
            size="medium"
            folderParentUuid={folderId}
            vaultUuid={finalVaultUuid}
        />

        <RenameDocumentModal
            isOpen={isRenameDocumentModalOpen}
            onClose={handleCloseRenameDocumentModal}
            onSave={handleSaveRenameDocument}
            documentUuid={
                documentToRename?.document_uuid ||
                documentToRename?.documentUuid ||
                documentToRename?.id
            }
            initialName={documentToRename?.name || ''}
        />

        {/* ✅ RenameFolderModal - Rename folder */}
        <RenameFolderModal 
            isOpen={isRenameFolderModalOpen}
            onClose={handleCloseRenameFolderModal}
            onSave={handleSaveRenameFolder}
            folderUuid={folderToRename?.folder_uuid}
            initialName={folderToRename?.name || ''}
        />

        {/* ✅ FolderInformationSlideout - Show folder information */}
        <FolderInformationSlideout
            open={isFolderInfoOpen}
            onClose={() => setIsFolderInfoOpen(false)}
            folderName={selectedFolder?.name}
            ownerName={selectedFolder?.owner_full_name || selectedFolder?.owner || 'Unknown'}
        />
        
        <input {...fileInputProps} ref={fileInputRef} />

        <DeleteDocumentModal
            isOpen={activeFolderStore.isDeleteModalOpen}
            onClose={() => activeFolderStore.closeDeleteModal()}
            onConfirm={handleConfirmDelete}
            documentName={activeFolderStore.documentToDelete?.name || ''}
            isDeleting={activeFolderStore.isDeletingDocument}
        />
        </>
    );
};

const ActiveFolder = observer(ActiveFolderComponent);
export default ActiveFolder;
